<template>
  <div class="result-item">
    <v-checkbox-btn
      v-if="selectable"
      class="result-checkbox"
      density="compact"
      :model-value="isSelected"
      :disabled="disableSelect"
      @click.stop
      @update:model-value="toggleSelection"
    />
    <div class="result-content">
    <!-- Row 1: title -->
    <div class="result-row-1">
      <span class="result-title-wrap" :class="{ 'result-title-wrap--badged': pendingState }">
        <router-link
          :to="filters.entityZoomLink(result.id)"
          class="result-title text-body-1 font-weight-medium text-decoration-none"
          :style="pendingTitleStyle"
          v-html="filters.prettyTitle(displayTitle)"
        />
        <span
          v-if="pendingState"
          class="result-pending-tag text-body-1 font-weight-medium"
          :style="pendingTagStyle"
        >[{{ pendingState }}]</span>
      </span>
    </div>

    <!-- Row 2: entity-specific metadata -->
    <div class="result-meta mt-1" v-if="hasMetadata">
      <template v-if="isWorks">
        <span v-if="result.publication_year">{{ result.publication_year }}</span>
        <template v-if="result.authorships?.length">
          <span> · </span>
          <work-authors-string :authorships="result.authorships" />
        </template>
        <template v-if="result.primary_location?.source?.display_name">
          <span> · </span>
          <span class="font-italic">{{ result.primary_location.source.display_name }}</span>
        </template>
        <template v-if="result.cited_by_count">
          <span> · </span>
          <v-tooltip location="top" aria-label="Citation count">
            <template v-slot:activator="{ props: citeTipProps }">
              <span v-bind="citeTipProps" class="cited-by" @click.stop="viewCitingPapers">
                <v-icon size="14" class="count-icon">mdi-format-quote-close</v-icon>
                {{ result.cited_by_count.toLocaleString() }}
              </span>
            </template>
            cited by {{ result.cited_by_count.toLocaleString() }} works
          </v-tooltip>
        </template>
      </template>
      <template v-else-if="myEntityType === 'languages'">
        <span v-if="endonym">{{ endonym }}</span>
        <span v-if="endonym"> · </span>
        <v-tooltip location="top">
          <template #activator="{ props: isoProps }">
            <span v-bind="isoProps" style="cursor: help;">{{ isoCode }}</span>
          </template>
          ISO 639-1 language code
        </v-tooltip>
      </template>
      <template v-else>
        {{ unworkSubheader }}
      </template>
    </div>

    <!-- Row 3 (authors only): top topics -->
    <div v-if="topTopics.length" class="result-topics mt-1">
      <template v-for="(topic, i) in topTopics" :key="i">
        <span v-if="i > 0" class="topic-sep"> · </span>
        <span>{{ topic }}</span>
      </template>
    </div>



    </div>

    <!-- Right column: works count (non-works) -->
    <div v-if="asideKind" class="result-aside">
      <div class="result-stats">
        <v-icon size="14" class="count-icon">mdi-file-document-outline</v-icon>
        <span class="text-body-2">{{ countValue.toLocaleString() }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

import { useRouter } from 'vue-router';

import { url } from '@/url';
import filters from '@/filters';
import { createSimpleFilter, filtersAsUrlStr } from '@/filterConfigs';
import * as openalexId from '@/openalexId';
import { getEntityConfig, getLocationString } from '@/entityConfigs';
import countryCodeLookup from 'country-code-lookup';
import languageEndonyms from '@/languageEndonyms';

import WorkAuthorsString from '@/components/WorkAuthorsString.vue';


defineOptions({
  name: 'SerpResultsListItem',
});

const props = defineProps({
  result: Object,
  showIcon: Boolean,
  pendingState: { type: String, default: null }, // 'add' | 'remove' | null
  disableSelect: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
});

const store = useStore();
const router = useRouter();

// Inline !important: the SERP title <a> is colored by a higher-priority
// global anchor rule that beats Vuetify text-* helper classes. oxjob #187.
const pendingTitleStyle = computed(() => {
  if (props.pendingState === 'add') return 'color:#2e7d32 !important;';
  if (props.pendingState === 'remove') {
    return 'color:#c62828 !important;text-decoration:line-through !important;';
  }
  return '';
});

// Same color as the title, but never struck through (the "[remove]" tag
// stays legible).
const pendingTagStyle = computed(() => {
  if (props.pendingState === 'add') return 'color:#2e7d32;';
  if (props.pendingState === 'remove') return 'color:#c62828;';
  return '';
});

const entityType = computed(() => store.getters['entityType']);
const myEntityType = computed(() => openalexId.getEntityType(props.result.id));
const isWorks = computed(() => myEntityType.value === 'works');
const countValue = computed(() => props.result.works_count);

const isAuthors = computed(() => myEntityType.value === 'authors');
const topTopics = computed(() => {
  if (!isAuthors.value) return [];
  return (props.result.topics || [])
    .slice(0, 3)
    .map(t => t?.display_name)
    .filter(Boolean);
});

const asideKind = computed(() => {
  if (isWorks.value) return null;
  return countValue.value ? 'count' : null;
});

// Display title - use award-specific fallback for awards, otherwise use display_name
const displayTitle = computed(() => {
  if (myEntityType.value === 'awards') {
    return filters.getAwardDisplayTitle(props.result);
  }
  return props.result.display_name || 'Untitled';
});

const formatAwardAmount = (amount, currency) => {
  if (!amount) return null;
  const currencySymbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥', CNY: '¥' };
  const symbol = currencySymbols[currency] || (currency ? `${currency} ` : '$');
  const units = [
    { value: 1e12, suffix: 'T' },
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'k' },
  ];
  for (const unit of units) {
    if (amount >= unit.value) {
      const scaled = amount / unit.value;
      const formatted = scaled < 10 ? scaled.toFixed(1).replace(/\.0$/, '') : Math.round(scaled);
      return `${symbol}${formatted}${unit.suffix}`;
    }
  }
  return `${symbol}${Math.round(amount)}`;
};

const countryName = (code) => {
  if (!code) return null;
  try {
    return countryCodeLookup.byIso(code)?.country || code;
  } catch {
    return code;
  }
};

const CONTINENT_SUBHEADERS = {
  "Africa": "58 countries including Egypt and island nations like Mauritius",
  "Asia": "54 countries including Turkey, Cyprus, and Indonesia",
  "Europe": "52 countries including Russia but not Turkey",
  "North America": "42 countries including Central America and the Caribbean",
  "South America": "15 countries",
  "Oceania": "25 countries including Papua New Guinea but not Indonesia",
  "Antarctica": "Inconveniently located, quite cold",
};

const continentSubheader = (name) => {
  return CONTINENT_SUBHEADERS[name] || null;
};

const isoCode = computed(() => {
  const id = props.result?.id || '';
  return id.split('/').pop() || '';
});

const endonym = computed(() => {
  return languageEndonyms[isoCode.value] || null;
});

const formatAwardYears = (startYear, endYear) => {
  if (!startYear) return null;
  if (!endYear || startYear === endYear) return String(startYear);
  return `${startYear}-${endYear}`;
};

const unworkSubheader = computed(() => {
  const r = props.result;

  const factsToShow = {
    works: undefined,
    authors: [
      r.last_known_institutions?.[0]?.display_name,
    ],
    sources: [
      r.type,
      r.host_organization_name,
      r.is_oa ? 'open access' : 'toll-access',
    ],
    institutions: [
      getLocationString(r),
      r.type,
    ],
    awards: [
      formatAwardAmount(r.amount, r.currency),
      r.funder?.display_name,
      formatAwardYears(r.start_year, r.end_year),
    ],
    funders: [
      countryName(r.country_code),
      r.description,
    ],
    topics: [
      r.subfield?.display_name,
      r.field?.display_name,
      r.domain?.display_name,
    ],
    subfields: [
      r.field?.display_name,
      r.domain?.display_name,
    ],
    fields: [
      r.domain?.display_name,
    ],
    domains: [
      r.description,
    ],
    types: [
      r.description,
    ],
    continents: [continentSubheader(r.display_name)],
    publishers: [
      r.parent_publisher?.display_name,
      r.country_codes?.map(countryName).filter(Boolean).join(', '),
    ],
    keywords: [],
    countries: [
      r.continent?.display_name,
      r.is_global_south ? 'Global South' : null,
    ],
    languages: [],
    sdgs: [
      r.description,
    ],
    "source-types": [r.description],
    "institution-types": [r.description],
    licenses: [r.description],
    "oa-statuses": [r.description],
  };

  return (factsToShow[myEntityType.value] || [])
    .filter(f => !!f)
    .join(' · ');
});

const hasMetadata = computed(() => {
  if (isWorks.value) {
    return props.result.publication_year || props.result.authorships?.length || props.result.primary_location?.source?.display_name;
  }
  if (myEntityType.value === 'languages') return true;
  return !!unworkSubheader.value;
});

// Methods
function viewCitingPapers() {
  const citesFilter = createSimpleFilter('works', 'cites', props.result.id);
  router.push({
    name: 'Serp',
    params: { entityType: 'works' },
    query: { filter: filtersAsUrlStr([citesFilter]) },
  });
}

function viewWorks() {
  const filterKey = getEntityConfig(myEntityType.value).filterKey;
  const worksFilter = createSimpleFilter('works', filterKey, props.result.id);
  url.pushNewFilters([worksFilter], 'works');
}

const isSelected = computed(() => store.getters['selection/isSelected'](props.result.id));
function toggleSelection() {
  store.commit('selection/toggleId', props.result.id);
}
</script>


<style scoped>
.result-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.result-item:last-child {
  border-bottom: none;
}

.result-checkbox {
  flex: 0 0 auto;
  width: auto;
  margin-top: -2px;
  margin-left: -4px;
}

.result-content {
  flex: 1 1 0;
  min-width: 0;
}

.result-row-1 {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.result-title-wrap {
  flex: 1 1 0;
  min-width: 0;
}

.result-title-wrap--badged {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.result-title-wrap--badged .result-title {
  flex: 0 1 auto;
}

.result-title {
  flex: 1 1 0;
  min-width: 0;
  display: block;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.87);
}

.result-title:hover {
  text-decoration: underline;
}

.result-pending-tag {
  flex: 0 0 auto;
  white-space: nowrap;
}

.result-aside {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 56px;
}

.result-stats {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
  white-space: nowrap;
}

.result-meta {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

.result-topics {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.55);
}

.topic-sep {
  color: rgba(0, 0, 0, 0.35);
}

.count-icon {
  opacity: 0.35;
}

.cited-by {
  white-space: nowrap;
  cursor: pointer;
}

.cited-by:hover {
  text-decoration: underline;
}

.pdf-btn {
  font-weight: 600;
  letter-spacing: 0.02em;
  text-decoration: none;
}
</style>
